import React, { useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { toast } from "../../utils/toast";
import { useSubmitGuard } from "../../utils/useSubmitGuard";
import { errorMessage } from "../../utils/errorMessage";
import { API_DETAILS, COOKIE_KEYS } from "../../App";
import MembershipPanel from "./MembershipPanel";
import PaymentHistoryPanel from "./PaymentHistoryPanel";

/**
 * Billing: the membership, what has been charged for it, and promo codes.
 *
 * These used to sit at the bottom of Personal Information, with the Billing menu
 * item opening that same screen and scrolling down. That made Billing and
 * Personal Information the same destination, which is not what either name
 * promises — so billing is its own view and the profile is only the profile.
 */

const BillingView = (props) => {
  const uId = Cookies.get(COOKIE_KEYS.userId);

  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [membershipVersion, setMembershipVersion] = useState(0);
  const [, guard] = useSubmitGuard();

  const applyPromoCode = guard(async () => {
    const code = promoCode.trim();
    if (!code || promoLoading) return;

    const url =
      API_DETAILS.baseUrl +
      API_DETAILS.PORT +
      API_DETAILS.baseExtension +
      `/apply-promo`;

    setPromoLoading(true);
    try {
      // In the body: a code in the URL is kept in access logs and history.
      await axios.post(url, { userId: uId, code }, {
        headers: { "Content-Type": "application/json" },
      });
      toast.success("Promo code applied. Your membership is now active.");
      setPromoCode("");
      // The membership panel reads its state when it mounts, so remount it. A
      // full page reload did the same and dropped the patient back on Home.
      setMembershipVersion((v) => v + 1);
    } catch (err) {
      const message =
        errorMessage(err, "We couldn't apply that promo code. Check the code and try again.");
      toast.error(message);
    } finally {
      setPromoLoading(false);
    }
  });

  const card = {
    padding: "24px",
    borderRadius: "10px",
    backgroundColor: "var(--card)",
    border: "1px solid var(--border)",
  };

  return (
    <div style={{ maxWidth: "896px", margin: "0 auto", padding: "32px", width: "100%" }}>

      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "32px" }}>
        <button
          aria-label="Back to profile menu"
          onClick={() => props.openProfileMenu?.()}
          style={{
            padding: "8px",
            borderRadius: "8px",
            border: "1px solid var(--border)",
            backgroundColor: "var(--card)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m12 19-7-7 7-7" /><path d="M19 12H5" />
          </svg>
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 600 }}>Membership</h2>
          <p style={{ margin: "4px 0 0", opacity: 0.7, fontSize: "0.875rem" }}>
            Your plan, payments and refunds
          </p>
        </div>
      </div>

      <MembershipPanel key={membershipVersion} />

      <PaymentHistoryPanel />

      <div style={card}>
        <h3 style={{ margin: 0, fontFamily: "var(--font-heading)", fontSize: "20px", fontWeight: 600 }}>
          Promo Code
        </h3>
        <p style={{ margin: "8px 0 16px", fontSize: "14px", color: "var(--youro-charcoal)", opacity: 0.75 }}>
          Have a code? Enter it here to activate a membership without payment.
        </p>
        <div style={{ display: "flex", gap: "10px", width: "100%", alignItems: "center" }}>
          <input
            aria-label="Promo code"
            className="input-field input-border"
            type="text"
            placeholder="Enter promo code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            disabled={promoLoading}
          />
          <button
            className="btn-filled"
            type="button"
            onClick={applyPromoCode}
            disabled={promoLoading || !promoCode.trim()}
            style={{ whiteSpace: "nowrap" }}
          >
            {promoLoading ? "Applying..." : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillingView;
